import {
  Box,
  Button,
  RadioList,
  RadioOnValue,
  RadioValue,
  Text,
} from '@bonanza-social/moonlight';
import { BottomSheet, BottomSheetExtendedProps } from '@bonanza-social/navigation';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@bonanza-social/i18n';
import { getCloseAccountOptions } from './CloseAccount.utils';
import { AccountService } from '../../api';

export type CloseAccountProps = BottomSheetExtendedProps;

function CloseAccount({ close }: CloseAccountProps) {
  const [t] = useTranslation('account');
  const closeAccountOptions = useMemo(() => getCloseAccountOptions(t), [t]);

  const [reason, setReason] = useState<RadioValue>();
  const [showError, setShowError] = useState(false);
  const [closing, setClosing] = useState(false);

  const onReason: RadioOnValue = useCallback(
    (value) => {
      setShowError(false);
      setReason(value);
    },
    [setReason],
  );

  const onCloseAccount = useCallback(async () => {
    if (!reason) return setShowError(true);

    setClosing(true);
    try {
      await AccountService.closeAccount({ data: { reason } });
    } catch {
      BottomSheet.open({
        namespace: 'common',
        type: 'Info',
        titleNS: 'account',
        titleKey: 'myProfileErrorTitle',
        data: {
          textNS: 'account',
          textKey: 'myProfileErrorMessage',
        },
      });
    } finally {
      setClosing(false);
      close();
    }
  }, [reason, close]);

  return (
    <Box gap="space-600">
      <Text
        locNS="account"
        locKey="myProfileCloseAccountMessage"
        variant="body-m"
        color="darkSurfaceMuted"
        marginTop="space-600"
      />
      <Box gap="space-400">
        <RadioList
          options={closeAccountOptions}
          value={reason}
          onValue={onReason}
        />
        {showError ? (
          <Text
            locNS="account"
            locKey="myProfileCloseAccountReasonError"
            variant="body-s"
            color="darkSurfaceError"
          />
        ) : null}
      </Box>
      <Box flexDirection="row" gap="space-200">
        <Button
          flex={1}
          variant="primary"
          locNS="account"
          locKey="myProfileCloseAccountCancel"
          onPress={close}
        />
        <Button
          flex={1}
          variant="secondary"
          locNS="account"
          locKey="myProfileCloseAccountCta"
          onPress={onCloseAccount}
          loading={closing}
        />
      </Box>
    </Box>
  );
}

export default memo(CloseAccount);
